import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Select from 'react-select';

import Loading from "../../../components/Loading";
import Data from "../../../../repository";
import { createTransactionSchema } from '../../../../validation/schemas';

import styles from '../../../../styles/Admin.module.scss';

function CreateTransactionForm() {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const [users, setUsers] = useState([]);
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [serverError, setServerError] = useState(null);

	const {
		register,
		handleSubmit,
		control,
		formState: { errors },
		setValue
	} = useForm({
		resolver: yupResolver(createTransactionSchema),
		defaultValues: {
			user: '',
			adjustment: '',
			note: ''
		}
	});

	useEffect(() => {
		fetchUsers();
	}, []);

	const fetchUsers = async () => {
		try {
			const result = await Data.fetchUsers();
			setUsers(result.data);

			// Pre-select user from URL if provided
			if (searchParams.get("user")) {
				const selected = result.data.find(user => user._id === searchParams.get("user"));
				if (selected) {
					setValue('user', selected._id);
				}
			}
		} catch (error) {
			console.error('Error fetching users:', error);
		} finally {
			setLoading(false);
		}
	}

	const onSubmit = async (data) => {
		setSubmitting(true);
		setServerError(null);

		try {
			await Data.createTransaction({
				user: data.user,
				adjustment: Number(data.adjustment),
				note: data.note
			});
			navigate('/admin/transactions');
		} catch (error) {
			setServerError(error.response?.data?.message || 'Failed to create transaction. Please try again.');
			setSubmitting(false);
		}
	}

	if (loading) return <Loading button={false} />;

	const userOptions = users.map(user => ({
		value: user._id,
		label: `${user.firstName} ${user.lastName} (${user.email})`
	}));

	return (
		<form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
			<div className={styles.select}>
				<p>User: *</p>
				<Controller
					name="user"
					control={control}
					rules={{ required: true }}
					render={({ field }) => (
						<Select
							value={userOptions.find((o) => o.value === field.value) ?? null}
							onChange={(option) => field.onChange(option?.value ?? '')}
							onBlur={field.onBlur}
							options={userOptions}
							placeholder="Select user..."
							isClearable
						/>
					)}
				/>
				{errors.user && <span className="form-field-error">{errors.user.message}</span>}
			</div>

			<div>
				<p>Tokens: *</p>
				<input
					type="number"
					{...register('adjustment')}
					className={errors.adjustment ? 'input-error' : ''}
				/>
				{errors.adjustment && <span className="form-field-error">{errors.adjustment.message}</span>}
			</div>

			<div>
				<p>Notes:</p>
				<input type="text" {...register('note')} />
			</div>

			{serverError && <p style={{ color: 'red' }}>{serverError}</p>}

			<button type="submit" className="btn btnPrimary" disabled={submitting}>
				{submitting ?
					<Loading button={true} />
					:
					`Create Transaction`
				}
			</button>
		</form>
	)
}

export default CreateTransactionForm;
